<template>
  <div class="question-container">
    <!-- PREVIEW MODE -->
    <div v-if="mode == 0">
      <div class="row">
        <div class="col-md-5 d-flex pl-0">
          <span
            v-if="surveyData.showNumericLabels || surveyData.showNumericLabels == undefined || surveyData.showNumericLabels == null"
            class="index-no">{{ stepNo
            }}.</span>
          <template v-if="activityType == 'activity'">
            <span class="question-text" v-html="question.formattedBody"></span>
          </template>
          <template v-else>
            <span v-if="selectedLanguageCulture == 'EN'" class="question-text"
                  v-html="answer[usage.QUESTION_TEXT]"></span>
            <span v-else class="question-text" v-html="answer[usage.DISPLAY_QUESTION_TEXT]"></span>
          </template>
          <span v-if="question.isSkippable"
                class="activity-form-preview-components__optional-box ml-15 d-inline-box pt-2">Optional</span>
        </div>

        <div class="col-md-7" data-test="answer-preview">
          <template v-if="answer && answer.answerText">
            <div>{{ answerWithoutOther }}</div>

            <div v-if="otherAnswer" class="other-answer-display">
              <div class="other-answer-display-title">{{ otherOptionModel.label || "Other" }}:</div>
              <div class="other-answer-display-text">{{ otherAnswer }}</div>
            </div>
          </template>
        </div>

        <template v-if="answer && answer.answerText">
          <div v-if="showScore && activityType == 'activity' && answer.score != null"
               class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ answer.score }}
            </div>
          </div>
        </template>
        <template v-else-if="question.isSkippable">
          <div v-if="showScore && activityType == 'activity'" class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ question.skipScore || 0 }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- EDIT MODE -->
    <div v-if="mode == 1">
      <div class="row">
        <div class="col-md-5 d-flex pl-0">
          <span
            v-if="surveyData.showNumericLabels || surveyData.showNumericLabels == undefined || surveyData.showNumericLabels == null"
            :class="isQuestionSkipped() ? 'disable-question' : ''"
            class="index-no">{{ stepNo
            }}.</span>
          <template v-if="activityType == 'activity'">
            <span :class="isQuestionSkipped() ? 'disable-question' : ''" class="question-text"
                  v-html="question.formattedBody"></span>
          </template>
          <template v-else>
            <span v-if="selectedLanguageCulture == 'EN'" :class="isQuestionSkipped() ? 'disable-question' : ''"
                  class="question-text" v-html="answer[usage.QUESTION_TEXT]"></span>
            <span v-else :class="isQuestionSkipped() ? 'disable-question' : ''" class="question-text"
                  v-html="answer[usage.DISPLAY_QUESTION_TEXT]"></span>
          </template>
          <span v-if="question.isSkippable"
                :class="isQuestionSkipped() ? 'disable-question' : ''"
                class="activity-form-preview-components__optional-box ml-15 d-inline-box pt-2">Optional</span>
          <span v-if="isQuestionSkipped()" class="skipped-question-box ml-15 d-inline-box pt-2">Skipped </span>
        </div>
        <div :class="isQuestionSkipped() ? 'disable-question' : ''" class="col-md-7" data-test="answer-edit">
          <div v-if="question.subType === 'Single'">
            <b-form-group>
              <b-form-radio-group v-model="updatedAns[0]" v-validate="'required'" :disabled="isAnswerDisabled"
                                  :name="`multiChoice${question.id}`" stacked
                                  v-on:change="onChangeMultiOptions($event, question.subType)">
                <b-form-radio v-for="option in filteredOptions" :key="option.id" :value="option.id">
                  {{ option.body }}
                </b-form-radio>

                <div v-if="otherOptionModel.isEnabled">
                  <b-form-radio :key="otherOptionModel.id" :value="otherOptionModel.id">
                    {{ otherOptionModel.label }}
                  </b-form-radio>

                  <div v-if="otherOptionModel.hasComment && isOtherSelected"
                       :class="{ 'disabled-area': !isOtherSelected, 'active-area': isOtherSelected }"
                       class="other-option-comment">
                    <textarea v-model="otherOptionModel.value"
                              :disabled="!isOtherSelected"
                              :maxlength="otherOptionModel.commentMaxLength"
                              :placeholder="otherOptionModel.comment"
                              class="other-option-comment-area"
                              @keydown="preventInvalidCharacters"
                              @change="onOtherCommentChange">
                    </textarea>

                    <div :class="{'max-character-limit': otherCommentLength >= otherOptionModel.commentMaxLength}"
                         class="other-option-comment-count">
                      {{ otherCommentLength }} / {{ otherOptionModel.commentMaxLength }}
                    </div>
                  </div>
                </div>
              </b-form-radio-group>

              <span v-if="hasFieldWarning" class="text-warning pl-15 fw-bold">{{ fieldWarningText }} </span>
              <span v-if="hasFieldError" class="text-danger pl-15 fw-bold">{{ fieldErrorText }} </span>
            </b-form-group>
          </div>
          <div v-else-if="question.subType === 'Multi'">
            <b-form-group>
              <b-form-checkbox-group v-model="updatedAns" v-validate="'required'" :disabled="isAnswerDisabled"
                                     :name="`multiChoice${question.id}`" stacked
                                     v-on:change="onChangeMultiOptions($event, question.subType)">
                <b-form-checkbox v-for="option in filteredOptions" :key="option.id" :value="option.id">
                  {{ option.body }}
                </b-form-checkbox>

                <div v-if="otherOptionModel.isEnabled">
                  <b-form-checkbox :key="otherOptionModel.id" :value="otherOptionModel.id">
                    {{ otherOptionModel.label }}
                  </b-form-checkbox>

                  <div v-if="otherOptionModel.hasComment && isOtherSelected"
                       :class="{ 'disabled-area': !isOtherSelected, 'active-area': isOtherSelected }"
                       class="other-option-comment">
                    <textarea v-model="otherOptionModel.value"
                              :disabled="!isOtherSelected"
                              :maxlength="otherOptionModel.commentMaxLength"
                              :placeholder="otherOptionModel.comment"
                              class="other-option-comment-area"
                              @keydown="preventInvalidCharacters"
                              @change="onOtherCommentChange">
                    </textarea>

                    <div :class="{'max-character-limit': otherCommentLength >= otherOptionModel.commentMaxLength}"
                         class="other-option-comment-count">
                      {{ otherCommentLength }} / {{ otherOptionModel.commentMaxLength }}
                    </div>
                  </div>
                </div>
              </b-form-checkbox-group>
              <span v-if="hasFieldError" class="text-danger pl-15 fw-bold">{{ fieldErrorText }} </span>
            </b-form-group>
          </div>
        </div>

        <template v-if="answer && answer.answerText">
          <div v-if="showScore && activityType == 'activity' && answer.score != null"
               class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ answer.score }}
            </div>
          </div>
        </template>
        <template v-else-if="question.isSkippable">
          <div v-if="showScore && activityType == 'activity'" :class="isQuestionSkipped() ? 'disable-question' : ''"
               class="ml-25 d-inline-block pt-20">
            <label class="fs-12">Score</label>
            <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
              {{ question.skipScore || 0 }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- RESPONSE MODE -->
    <div v-else-if="mode == 2">
      <div class="row">
        <div class="col-md-5 d-flex pl-0">
          <div class="row">
            <div class="col-md-12 d-flex p-0 answers">
              <span v-if="answer && answer.edit_status == 1" class="changed"></span>
              <span
                v-if="surveyData.showNumericLabels || surveyData.showNumericLabels == undefined || surveyData.showNumericLabels == null"
                class="index-no">{{ stepNo
                }}.</span>
              <template v-if="activityType == 'activity'">
                <span class="question-text" v-html="question.formattedBody"></span>
              </template>
              <template v-else>
                <span v-if="selectedLanguageCulture == 'EN'" class="question-text"
                      v-html="answer[usage.QUESTION_TEXT]"></span>
                <span v-else class="question-text" v-html="answer[usage.DISPLAY_QUESTION_TEXT]"></span>
              </template>
              <span v-if="question.isSkippable"
                    class="activity-form-preview-components__optional-box ml-15 d-inline-box pt-2">Optional</span>
            </div>
            <!-- CURRENT ANSWER -->
            <template v-if="answer && answer.answer_text">
              <div class="col-md-12 d-flex pl-30">
                <div class="wrap" data-test="answer-response">
                  <div>{{ currentAnswerWithoutOther }}</div>

                  <div v-if="currentOtherAnswer" class="other-answer-display">
                    <div class="other-answer-display-title">{{ otherOptionModel.label || "Other" }}:</div>
                    <div class="other-answer-display-text">{{ currentOtherAnswer }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div v-if="showScore && activityType == 'activity' && answer.score != null"
                     class="d-inline-block ml-25 pt-10">
                  <label>Score</label>
                  <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                    {{ answer.score }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="question.isSkippable">
              <div v-if="showScore && activityType == 'activity'" class="ml-25 d-inline-block pt-20">
                <label class="fs-12">Score</label>
                <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                  {{ question.skipScore || 0 }}
                </div>
              </div>
            </template>
          </div>
        </div>
        <div v-if="answer && answer.edit_status == 1" class="col-md-7 previous-answer">
          <div>
            <span class="previous-value-mark"></span>
            <div class="previous-value ml-3">PREVIOUS RESPONSE</div>

            <template v-if="answer && answer.privius_answer_text">
              <div class="wrap">
                <div>{{ previousAnswerWithoutOther }}</div>

                <div v-if="previousOtherAnswer" class="other-answer-display">
                  <div class="other-answer-display-title">{{ otherOptionModel.label || "Other" }}:</div>
                  <div class="other-answer-display-text">{{ previousOtherAnswer }}</div>
                </div>
              </div>

              <div class="row">
                <div v-if="showScore && answer.privius_score != null" class="d-inline-block ml-0 pt-10">
                  <label>Score</label>
                  <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                    {{ answer.privius_score }}
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="question.isSkippable">
              <div v-if="showScore && activityType == 'activity'" class="ml-25 d-inline-block pt-20">
                <label class="fs-12">Score</label>
                <div class="ml-10 activity-form__score-box d-inline-block color_4c8ce4">
                  {{ question.skipScore || 0 }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import activityCommonMixin from "./activity-common-mixin";

export default {
  name: "MultiChoiceResponse",
  mixins: [activityCommonMixin],
  data() {
    return {
      updatedAns: [],
      hasFieldError: false,
      fieldErrorText: "",
      fieldWarningText: "",
      scoresMap: {},
      hasFieldWarning: "",
      noneOptionId: "",
      disableCheckBox: false,
      otherOptionModel: {}
    };
  },
  props: {
    mode: Number,
    answer: Object,
    question: Object,
    surveyData: Object,
    usage: Object,
    stepIndex: Number,
    selectedLanguageCulture: String,
    activityType: String
  },
  watch: {
    updatedAns() {
      this.updateAnswers();
    },
    hasFieldError(val) {
      this.question.hasFieldError = val;
    }
  },
  mounted() {
    if (this.answer) {
      if (this.mode === 2 && this.answer.option_id) {
        if (this.question.subType === "Single") {
          this.updatedAns[0] = this.answer.option_id;
        } else {
          this.updatedAns = this.answer.option_id.split(",");
        }
      } else if (this.answer.optionId) {
        this.insertOtherOption();
        if (this.question.subType === "Single") {
          this.updatedAns[0] = this.answer.optionId;
        } else {
          this.updatedAns = this.answer.optionId.split(",");
        }
      }

      if (this.mode !== 2) {
        this.otherOptionModel = {
          id: this.question.otherOptionId,
          isEnabled: this.question.isDisplayOtherOptionContentField === true,
          hasComment: this.question.isDisplayOtherCommentSelectedField === true,
          comment: this.question.otherCommentSelectedField,
          label: this.question.otherOptionContentField,
          commentMaxLength: 1000,
          value: ""
        };

        // Need to first initialize this.otherOptionModel before setting it´s value
        if (this.otherOptionModel.hasComment) {
          this.otherOptionModel.value = this.otherAnswer;
        }
      }
    }

    this.calculateScoresMap();
    this.updateAnswers();
  },
  computed: {
    isOtherSelected() {
      if (this.otherOptionModel.id == null) {
        return false;
      }

      return this.updatedAns.includes(this.otherOptionModel.id);
    },
    isAnswerDisabled() {
      return this.answer ? this.answer.isSkipped === true : false;
    },
    filteredOptions() {
      return this.question && this.question.options ? this.question.options.filter(q => q.id !== this.otherOptionModel.id) : [];
    },
    otherCommentLength() {
      return this.otherOptionModel.value ? this.otherOptionModel.value.length: 0;
    },
    answerWithoutOther() {
      if (!this.question || !this.question.options) {
        return "";
      }

      const filteredIds = this.question.options.filter(o => {
        if (o.id === this.otherOptionModel.id && this.otherOptionModel.hasComment) {
          return false;
        }

        return this.updatedAns.includes(o.id);
      });

      const answers = this.question.options.filter(o => filteredIds.includes(o)).map(o => o.body);
      return answers.filter(o => o != null).join(", ");
    },
    otherAnswer() {
      if (!this.question || !this.question.options) {
        return null;
      }

      let answer;

      if (!this.otherOptionModel.id || !this.otherOptionModel.hasComment || !this.answer.displayAnswerText) {
        return answer;
      }

      const sortedAnswers = this.question.options.map(o => o.id).filter(o => this.updatedAns.includes(o));
      const otherIdx = sortedAnswers.indexOf(this.otherOptionModel.id);
      let answers;

      if (this.answer.displayAnswerText instanceof Array) {
        answers = this.answer.displayAnswerText.length === this.answer.optionId.split(",").length
          ? this.answer.displayAnswerText
          : this.answer.displayAnswerText[0].split(",");
      } else {
        answers = this.answer.displayAnswerText.split(",");
      }

      if (otherIdx !== -1 && otherIdx === answers.length - 1) {
        const otherSplit = answers[otherIdx].split(":");

        if (otherSplit.length > 1) {
          answer = otherSplit[otherSplit.length - 1].trim();
        }
      }

      return answer;
    },
    currentAnswerWithoutOther() {
      const answer = this.selectedLanguageCulture === "EN" ? this.answer.answer_text : this.answer.display_answer_text;
      return this.getDisplayAnswerWithoutOther(answer, this.answer.option_id);
    },
    currentOtherAnswer() {
      const answer = this.selectedLanguageCulture === "EN" ? this.answer.answer_text : this.answer.display_answer_text;
      return this.getDisplayOtherAnswer(answer, this.answer.option_id);
    },
    previousAnswerWithoutOther() {
      const answer = this.selectedLanguageCulture === "EN" ? this.answer.privius_answer_text : this.answer.privius_display_answer_text;
      return this.getDisplayAnswerWithoutOther(answer, this.answer.privius_optionId);
    },
    previousOtherAnswer() {
      const answer = this.selectedLanguageCulture === "EN" ? this.answer.privius_answer_text : this.answer.privius_display_answer_text;
      return this.getDisplayOtherAnswer(answer, this.answer.privius_optionId);
    }
  },
  methods: {
    normalizeUpdatedAnswers() {
      if (!this.answer.optionId) {
        return [];
      }

      const optionIds = this.question.options.map(q => q.id);
      return this.updatedAns.filter(a => optionIds.includes(a));
    },
    onOtherCommentChange(event) {
      const value = event.target.value;

      if (value && typeof value === "string") {
        event.preventDefault();
        this.otherOptionModel.value = value.replace(/:/g, "");
        this.$forceUpdate();
        this.updateAnswers();
      }
    },
    getDisplayAnswerWithoutOther(answer, optionId) {
      if (!answer) {
        return null;
      }

      const answerArr = answer.split(",");
      const otherIdx = optionId.split(",").indexOf(this.question.otherOptionId);
      let result;

      if (otherIdx !== -1 && otherIdx === answerArr.length - 1 && this.question.isDisplayOtherCommentSelectedField) {
        result = answerArr.filter((a, idx) => idx !== otherIdx).join(", ");
      } else {
        result = answerArr.join(", ");
      }

      return result;
    },
    getDisplayOtherAnswer(answer, optionId) {
      if (!answer) {
        return null;
      }

      const answerArr = answer.split(",");
      let result;

      const otherIdx = optionId.split(",").indexOf(this.question.otherOptionId);

      if (otherIdx !== -1 && otherIdx === answerArr.length - 1 && this.question.isDisplayOtherCommentSelectedField) {
        const otherAnswer = answerArr.find((a, idx) => idx === otherIdx).split(":");
        result = otherAnswer.length > 1 ? otherAnswer[1].trim() : otherAnswer[0].trim();
      }

      return result;
    },
    setOtherAnswer(answer) {
      const option = this.question.options.find(o => o.id === this.otherOptionModel.id);

      if (!option) {
        return;
      }

      option.body = answer;
      option.formattedBody = answer;

      return option.body;
    },
    updateAnswers() {
      this.checkForFieldErrors();

      if (this.hasFieldError) {
        this.resetFields();
      } else if (this.question.options) {
        const selectedOption = this.question.options.filter(opt => opt.id === this.updatedAns[0])[0];

        if (this.answer) {
          if (selectedOption && this.question.subType === "Single") {
            if (selectedOption.id === this.otherOptionModel.id && this.otherOptionModel.isEnabled) {
              if (this.otherOptionModel.hasComment && !this.otherOptionModel.value && this.mode === 1) {
                this.hasFieldError = true;
                this.fieldErrorText = "Please enter a comment";
              } else if (this.otherOptionModel.hasComment && this.otherOptionModel.value) {
                selectedOption.body = this.setOtherAnswer(`${this.otherOptionModel.label || "Other"}: ${this.otherOptionModel.value}`);
              } else {
                selectedOption.body = this.setOtherAnswer(`${this.otherOptionModel.label || "Other"}`);
              }
            }

            this.answer.optionId = selectedOption.id;
            this.answer.answerText = selectedOption.body;
          } else if (this.question.subType === "Multi") {
            const arrMultiOptions = this.normalizeUpdatedAnswers();
            const hasOtherOption = arrMultiOptions.includes(this.otherOptionModel.id);
            const isOtherOptionRequired = hasOtherOption && this.otherOptionModel.isEnabled;

            if (isOtherOptionRequired) {
              if (this.otherOptionModel.hasComment && !this.otherOptionModel.value && this.mode === 1) {
                this.hasFieldError = true;
                this.fieldErrorText = this.otherOptionModel.comment;
              } else if (this.otherOptionModel.hasComment && this.otherOptionModel.value) {
                this.setOtherAnswer(`${this.otherOptionModel.label || "Other"}: ${this.otherOptionModel.value}`);
              } else {
                this.setOtherAnswer(`${this.otherOptionModel.label || "Other"}`);
              }
            }

            const multipleOptions = this.findAnsweredSequence().map(i => (i ? i.body : null));
            this.answer.optionId = arrMultiOptions.filter(a => a).join(",");
            this.answer.answerText = multipleOptions.filter(a => a !== null).join(",");
          }
        }
      }
    },
    preventInvalidCharacters(event) {
      if (event.key === ":") {
        event.preventDefault();
      }
    },
    resetFields() {
      this.answer.optionId = "";
      this.answer.answerText = "";
      this.answer.score = 0;
      this.answer.computedScore = 0;
    },
    onChangeMultiOptions(optionIds, questionType) {
      if (questionType === "Multi") {
        let noneOption = _.find(this.question.options, function(o) {
          return (o.formattedBody || "").toLowerCase() === "none";
        });
        if (noneOption) {
          this.noneOptionId = noneOption.id;
        }
        this.disableCheckBox = false;
        if (optionIds && optionIds.includes(this.noneOptionId)) {
          this.answer.optionsIds = [];
          this.answer.optionsIds.push(this.noneOptionId);
          this.updatedAns = [];
          this.updatedAns.push(this.noneOptionId);
          this.disableCheckBox = true;
        }
        if (optionIds instanceof Array) {
          this.answer.score = 0;
          optionIds.forEach(id => {
            for (let score in this.scoresMap) {
              if (score === id) {
                this.answer.score += this.scoresMap[score];
              }
            }
          });
        }
      } else {
        this.answer.score = this.scoresMap[optionIds];
      }
      this.$forceUpdate();
      this.answerUpdated();
    },
    calculateScoresMap() {
      if (this.question.options) {
        this.question.options.forEach((option) => {
          this.scoresMap[option.id] = option.score || 0;
        });
      }
    },
    answerUpdated() {
      this.$nextTick(() => {
        this.$emit(`answerChanged`, {
          questionId: this.question.id,
          newAns: this.normalizeUpdatedAnswers()
        });
      });
    },
    findAnsweredSequence() {
      return this.updatedAns.map(item => {
        return this.question.options.filter(opt => opt.id == item)[0];
      });
    },
    checkForFieldErrors() {
      this.reseterror();
      this.question.dataChecks = [];
      if (this.question.dataEditChecks) {
        let softDataCheck = false;
        for (let k = 0; k < this.question.dataEditChecks.length; k++) {
          if (this.question.dataEditChecks[k].check === "SOFT") {
            if (this.question.dataEditChecks[k].type === "NOT_BLANK" && this.updatedAns.length === 0) {
              this.question.isSkippable = true;
              this.$validator.validateAll().then(result => {
                if (!result) {
                  this.hasFieldWarning = true;
                  this.fieldWarningText = "This field is required.";
                  this.hasFieldError = true;
                  softDataCheck = true;
                  this.fieldErrorText = "This field is required.";
                }
              });
              this.question.dataChecks.push({
                dataCheckId: this.question.dataEditChecks[k].id,
                dataCheckType: this.question.dataEditChecks[k].type,
                message: `This field is required.`
              });
            }
          } else if (this.question.dataEditChecks[k].type === "NOT_BLANK" && this.updatedAns.length == 0) {
            this.question.isSkippable = false;
            this.$validator.validateAll().then(result => {
              if (!result) {
                this.hasFieldError = true;
                this.fieldErrorText = "This field is required.";
              }
            });
          }
        }
        if (softDataCheck) {
          return false;
        }
      } else {
        this.$validator.validateAll().then(result => {
          if (!result) {
            this.hasFieldError = true;
            this.fieldErrorText = "This field is required.";
          }
        });
      }
    },
    reseterror() {
      this.hasFieldError = false;
      this.hasFieldWarning = false;
    },
    insertOtherOption() {
      if (this.question.otherOptionId) {
        const sequence = this.question.options.length + 1;

        const option = {
          body: this.otherAnswer,
          colorCoding: {
            color: "#fff",
            warningMessage: null
          },
          enabled: this.question.isDisplayOtherOptionContentField === true,
          excludeScore: this.question.excludeScore,
          formattedBody: this.otherAnswer,
          id: this.question.otherOptionId,
          isAdvancedScoring: this.question.isAdvancedScoring,
          priority: 0,
          sendNotification: this.question.notifyToEmail,
          score: this.question.otherScore,
          sequence
        };

        this.question.options.push(option);
      }
    },
    isQuestionSkipped() {
      return this.answer.isSkipped;
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/edit-site-partcipant-activity.scss";

.changed {
  position: absolute;
  left: -29px;
  top: 0;
  width: 18px;
  height: 51px;
  border-radius: 1px;
  background-color: #f5a623;
}

.question-text {
  display: inline;
}

.previous-value-mark {
  width: 9px;
  height: 51px;
  background-color: #f5a623;
  border-radius: 1px;
  left: 0;
  top: 4px;
  position: absolute;
}

.previous-value {
  /* font-family: Roboto; */
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: #f5a623;
  margin-bottom: 9px;
  margin-top: 3px;
}

.answers {
  font-weight: 600;
  color: #000000;
}

.other-option-comment {
  position: relative;
  border-radius: 5px;
  box-shadow: 0 0 0 1px #4a4a4a;
  padding-bottom: 20px;
}

.disabled-area {
  background-color: rgb(245, 245, 245);
}

.active-area {
  box-shadow: 0 0 0 1px #007bff;
}

.other-option-comment-area {
  resize: none;
  width: 100%;
  padding: 10px;
  border: 0;
}

.other-option-comment-count {
  position: absolute;
  bottom: 5px;
  right: 10px;
  font-size: small;
}

.max-character-limit {
  color: crimson;
}

.other-answer-display {
  margin-top: 15px;
}

.other-answer-display-title {
  font-weight: bold;
  color: black;
}

.other-answer-display-text {
  font-style: italic;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-all;
  word-break: break-word;
  hyphens: auto;
}
</style>
